<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['change']);

const { t } = useI18n();
const topics = useMapGetter('topics/getTopics');
const chatTopicFilter = useMapGetter('getChatTopicFilter');

const isOpen = ref(false);
const searchQuery = ref('');
const searchInput = ref(null);

const closeAll = () => {
  isOpen.value = false;
  searchQuery.value = '';
};

watch(isOpen, open => {
  if (open) {
    nextTick(() => searchInput.value?.focus());
  }
});

const activeTopicLabel = computed(() => {
  if (!chatTopicFilter.value) return t('CHAT_LIST.CHAT_TOPIC_FILTER_ITEMS.all.TEXT');
  return (
    topics.value.find(topic => topic.id === chatTopicFilter.value)?.name ||
    t('CHAT_LIST.CHAT_TOPIC_FILTER_ITEMS.all.TEXT')
  );
});

const filteredTopics = computed(() => {
  const search = searchQuery.value.trim().toLowerCase();
  if (!search) return topics.value;
  return topics.value.filter(topic =>
    topic.name.toLowerCase().includes(search)
  );
});

const selectTopic = value => {
  emit('change', value);
  closeAll();
};
</script>

<template>
  <div v-on-click-outside="closeAll" class="relative">
    <NextButton
      v-tooltip.right="$t('CHAT_LIST.CHAT_SORT.TOPIC')"
      slate
      faded
      xs
      icon="i-lucide-tag"
      :label="activeTopicLabel"
      @click="isOpen = !isOpen"
    />
    <div
      v-if="isOpen"
      class="absolute z-40 mt-1 top-full w-56 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-xl p-1.5 flex flex-col gap-0.5"
      :class="{
        'ltr:left-0 rtl:right-0': !isOnExpandedLayout,
        'ltr:right-0 rtl:left-0': isOnExpandedLayout,
      }"
    >
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        :placeholder="$t('CHAT_LIST.CHAT_TOPIC_FILTER_ITEMS.SEARCH_PLACEHOLDER')"
        class="reset-base w-full h-8 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 px-2 mb-0.5 flex-shrink-0"
      />
      <div class="flex flex-col gap-0.5 max-h-56 overflow-y-auto">
        <button
          v-if="!searchQuery"
          type="button"
          class="text-left text-sm px-2 py-1.5 rounded-md hover:bg-n-alpha-1 flex-shrink-0"
          :class="!chatTopicFilter ? 'text-n-blue-11 font-medium' : 'text-n-slate-12'"
          @click="selectTopic('')"
        >
          {{ $t('CHAT_LIST.CHAT_TOPIC_FILTER_ITEMS.all.TEXT') }}
        </button>
        <button
          v-for="topic in filteredTopics"
          :key="topic.id"
          type="button"
          class="text-left text-sm px-2 py-1.5 rounded-md hover:bg-n-alpha-1 flex-shrink-0"
          :class="
            topic.id === chatTopicFilter
              ? 'text-n-blue-11 font-medium'
              : 'text-n-slate-12'
          "
          @click="selectTopic(topic.id)"
        >
          {{ topic.name }}
        </button>
        <p
          v-if="searchQuery && !filteredTopics.length"
          class="text-sm text-n-slate-11 px-2 py-1.5 mb-0"
        >
          {{ $t('CHAT_LIST.CHAT_TOPIC_FILTER_ITEMS.NO_RESULTS') }}
        </p>
      </div>
    </div>
  </div>
</template>
